import type React from "react";
import {useState} from "react";
import {Skeleton, Stack} from "@mui/material";
import {ClickToEdit} from "~/components/ClickToEdit/ClickToEdit";
import {notificationStore} from "~/stores/init";
import {useUpdateSupplierInfoMutation} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useUpdateSupplierInfoMutation";
import {currentInfoValue, type SupplierSummaryEntry} from "~/pages/supplier-360/types/Supplier360Types";

const PLACEHOLDER = 'No supplier description available yet.';

type SupplierInfoFieldProps = {
    entityId: number;
    /** `SupplierEntity.summary` (§2.1, mapping corrected 2026-10-05) — one entry per source, `[0]` is current. */
    summary: SupplierSummaryEntry[];
    /** Only the Storybook cases need this — the entity is already loaded wherever this renders. */
    isLoading?: boolean;
};

export const SupplierInfoField: React.FC<SupplierInfoFieldProps> = ({entityId, summary, isLoading = false}) => {
    const {mutate} = useUpdateSupplierInfoMutation();
    const value = currentInfoValue(summary);

    // Shown immediately on save; the mutation writes the server's updated entity back into the query.
    const [displayText, setDisplayText] = useState(value);
    const [prevValue, setPrevValue] = useState(value);
    if (prevValue !== value) {
        setPrevValue(value);
        setDisplayText(value);
    }

    if (isLoading) return <Skeleton variant="text" width="100%" height={60}/>;

    const onSave = (next: string) => {
        const trimmed = next.trim();
        if (trimmed === displayText.trim()) return;

        const previous = displayText;
        setDisplayText(trimmed);
        mutate(
            {entityId, summary: trimmed},
            {
                onError: () => {
                    setDisplayText(previous);
                    notificationStore.show("Could not save the supplier description. Please try again.", {
                        severity: "error",
                        id: `supplier-info-error-${entityId}`,
                        autoHideDuration: 3000,
                    });
                },
            },
        );
    };

    return (
        <Stack gap={0.5}>
            <ClickToEdit
                text={displayText}
                onSave={onSave}
                placeholder={PLACEHOLDER}
                isParagraph
                typographyProps={{variant: 'body2', color: 'grey.800'}}
                sx={{p: 1.5, borderColor: 'grey.300', borderRadius: 1, border: '1px solid'}}
            />
        </Stack>
    );
};
